import { useState, useEffect, useCallback } from "react";
import { faCode } from "@fortawesome/free-solid-svg-icons";
import JSONPretty from "react-json-pretty";
import { withAppHOC } from "../../hoc";
import PageHeading from "../../components/PageHeader/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import StatusHighliter from "../../components/StatusHighliter/StatusHighliter";
import {
  MessageBox,
  CustomButton,
  Label,
  Fieldset,
  TextInput,
  Button,
  Form,
  ResetButton,
} from "../../components/FormElements";
import { TableBuilder } from "../../components/TableElements";
import Modal from "../../components/Modal";
import {
  StyledFiltersContainer,
  StyledModalContainer,
  StyledBodyContainer,
  StyledHeading,
  StyledContent,
  StyledApiCallLogsContainer,
  StyledSpan,
} from "../../components/Styled";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from "../../constants";
import {
  getSearchParams,
  formatCurrency,
  formatCurrency4Decimal,
  shouldRedirect,
  redirectToPageOne,
} from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";
import Accordination from "../../components/Accordination/Accordination";
import { Grid, GridItem } from "../../components/Grid";

const SearchRechargeTransactionsList = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 100,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [rechargeNumber, setRechargeNumber] = useState("");
  const [modalData, setModalData] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus("");
    setResponseMessage("");
  };

  const resetFilters = () => {
    setRechargeNumber("");
    readData();
  };

  const readData = useCallback(
    (rechargeNumber = "") => {
      if (!rechargeNumber) {
        return { data: [], totalRecords: 0 };
      }

      setIsLoading(true);
      setResponseStatus("");
      setResponseMessage("");
      API.get(
        `/transactions/recharge-transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&rechargeNumber=${rechargeNumber}`
      )
        .then((response) => {
          const { status, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.SUCCESS) {
              setTotalRecords(pageInfo.totalRecords);
            }
            setTransactions(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILED);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const readLogs = (id) => {
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.get(`/transactions/logs/${id}`)
      .then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setModalData(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const toggleModal = (id) => {
    setShowModal(true);
    readLogs(id);
  };

  const reloadData = () => {
    readData(rechargeNumber);
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(rechargeNumber);
    }
  }, [paginationData, readData]);

  const { accountsPermissions } = props;
  const canTrack =
    accountsPermissions.includes(PERMISSION_TYPES.TRACK) || false;

  return (
    <>
      <PageHeading {...props} reloadData={reloadData} />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? "show" : ""}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>Account/Mobile</Label>
            <TextInput
              value={rechargeNumber}
              onChange={setRechargeNumber}
              placeholder="Account/Mobile"
              maxLength={1000}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton
              disabled={isLoading}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={
          !showModal &&
          responseStatus === "" &&
          transactions?.length === 0 &&
          isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILED &&
          !isLoading &&
          transactions?.length === 0
        }
        message={`No transactions found`}
      />
      {!showModal && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {transactions?.length > 0 && (
        <>
          <TableBuilder
            isLoading={!showModal && transactions?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: "",
                dataSelector: "transactionsId",
                sticky: true,
                canSort: false,
                width: "100px",
                CellRenderer: (value, item) => (
                  <>
                    {canTrack && (
                      <>
                        <CustomButton
                          icon={faCode}
                          onClick={() => toggleModal(value)}
                          style={{ color: "orange" }}
                        />
                      </>
                    )}
                  </>
                ),
              },
              {
                title: "Date & Time",
                dataSelector: "createdOn",
                dataType: "string",
              },
              {
                title: "Transaction Id",
                dataSelector: "transactionsId",
                dataType: "string",
              },
              {
                title: "Buyer",
                dataSelector: "buyerName",
                dataType: "string",
                CellRenderer: (value, item) => (
                  <>
                    {value}
                    <StyledSpan>{item.buyersMobileNumber}</StyledSpan>
                  </>
                ),
              },
              {
                title: "Seller",
                dataSelector: "sellersName",
                dataType: "string",
                CellRenderer: (value, item) => (
                  <>
                    {value}
                    <StyledSpan>{item.sellersMobileNumber}</StyledSpan>
                  </>
                ),
              },
              {
                title: "Account/Mobile",
                dataSelector: "rechargeNumber",
                dataType: "string",
              },
              {
                title: "Amount",
                dataSelector: "rechargeAmount",
                dataType: "number",
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: "Operator",
                dataSelector: "operatorName",
                dataType: "string",
              },
              {
                title: "Circle",
                dataSelector: "circleName",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              {
                title: "Category",
                dataSelector: "category",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              // {
              //   title: "Type",
              //   dataSelector: "transactionType",
              //   dataType: "string",
              // },
              {
                title: "API",
                dataSelector: "sellersApisName",
                dataType: "string",
              },
              {
                title: "Status",
                dataSelector: "rechargeStatus",
                dataType: "string",
                align: "center",
                CellRenderer: (value) => (
                  <StatusHighliter
                    className={
                      value === "Success"
                        ? "green"
                        : value === "Failed"
                        ? "red"
                        : "yellow"
                    }
                  >
                    {value}
                  </StatusHighliter>
                ),
              },
              {
                title: "Complaint Status",
                dataSelector: "processedComplaintStatus",
                dataType: "string",
                align: "center",
                CellRenderer: (value) => (
                  <StatusHighliter
                    className={
                      value === "Approved"
                        ? "green"
                        : value === "Rejected"
                        ? "red"
                        : "yellow"
                    }
                  >
                    {value}
                  </StatusHighliter>
                ),
              },
              {
                title: "Operators Id",
                dataSelector: "rechargeOperatorsId",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              {
                title: "Buyer Debit Amount",
                dataSelector: "buyerDebitAmount",
                dataType: "number",
                CellRenderer: (value) => formatCurrency4Decimal(value),
              },
              {
                title: "Seller Credit Amount",
                dataSelector: "sellerCreditAmount",
                dataType: "number",
                CellRenderer: (value) => formatCurrency4Decimal(value),
              },
              {
                title: "Profit",
                dataSelector: "adminProfit",
                dataType: "number",
                CellRenderer: (value) => formatCurrency4Decimal(value),
              },
            ]}
            tableData={transactions}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
          <Modal
            modalStatus={showModal}
            setModalStatus={() => {
              resetResponseData();
              setShowModal(false);
              setModalData([]);
            }}
            hideCloseButton
          >
            <StyledModalContainer width={`1200px`}>
              <h3>API Call Logs</h3>
              <StyledApiCallLogsContainer>
                <LoadingSpinner
                  isLoading={
                    responseStatus === "" &&
                    modalData?.length === 0 &&
                    isLoading
                  }
                />
                <NoData
                  status={
                    responseStatus !== API_RESPONSE_TYPES.FAILED &&
                    !isLoading &&
                    modalData?.length === 0
                  }
                  message={`No api logs found`}
                />
                <MessageBox status={responseStatus} message={responseMessage} />
                {modalData?.length > 0 &&
                  modalData.map(
                    (
                      {
                        requestType,
                        request,
                        requestOn,
                        requestBody,
                        response,
                        responseOn,
                      },
                      index
                    ) => (
                      <Accordination
                        key={index}
                        header={`${requestType}`}
                        autoExpanded={true}
                      >
                        <Grid columns={"calc(50% - 10px) calc(50% - 10px)"}>
                          <GridItem>
                            <StyledBodyContainer>
                              <StyledHeading>
                                API Request {requestOn && `( ${requestOn} )`}
                              </StyledHeading>
                              <StyledContent>{request}</StyledContent>
                            </StyledBodyContainer>
                            {requestBody && (
                              <StyledBodyContainer>
                                <StyledHeading>API Request Body</StyledHeading>
                                <StyledContent>{requestBody}</StyledContent>
                              </StyledBodyContainer>
                            )}
                          </GridItem>
                          <GridItem>
                            <StyledBodyContainer>
                              <StyledHeading>
                                API Response {responseOn && `( ${responseOn} )`}
                              </StyledHeading>
                              <StyledContent>
                                <JSONPretty
                                  id="json-pretty"
                                  data={response}
                                ></JSONPretty>
                              </StyledContent>
                            </StyledBodyContainer>
                          </GridItem>
                        </Grid>
                      </Accordination>
                    )
                  )}
              </StyledApiCallLogsContainer>
            </StyledModalContainer>
          </Modal>
        </>
      )}
    </>
  );
};

export default withAppHOC(SearchRechargeTransactionsList);
